/**
 * Planner Home: prompt composer, examples and recent work (PRD 10.2).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PlanResponse } from '../lib/types'
import { moneyShort, shortDate } from '../lib/format'
import { Callout, EmptyState, ErrorCallout } from '../components/Callout'
import { Skeleton } from '../components/Metric'

const EXAMPLES = [
  {
    title: 'Product launch, tight budget',
    text:
      'Launch campaign for Zeptra quick commerce in Bengaluru targeting 18-30 year olds, mid to ' +
      'premium. Budget 10 lakh. One month starting 1 November. Prefer billboards and Bus Shelters ' +
      'near IT parks and colleges, at least 6 sites.',
  },
  {
    title: 'Footfall drive, POI led',
    text:
      'Footfall campaign for a new store in Bengaluru around Koramangala and Jayanagar, near ' +
      'malls and food courts. 12 lakh, two weeks from 15 December, students and young ' +
      'professionals.',
  },
  {
    title: 'Digital, daypart sensitive',
    text:
      'Awareness campaign on digital screens in Whitefield and Electronic City for a fintech ' +
      'brand. Salaried commuters 25-40. 25 lakh, one month from 1 December.',
  },
  {
    title: 'Deliberately incomplete',
    text: 'We need some hoardings in Mumbai for a new drink.',
  },
]

export function PlannerHome() {
  const navigate = useNavigate()
  const [text, setText] = useState('')

  const plans = useQuery<PlanResponse[]>({ queryKey: ['plans'], queryFn: api.listPlans })

  const parse = useMutation({
    mutationFn: (brief: string) => api.parseBrief(brief),
    onSuccess: (result) => navigate(`/brief/${result.brief_id}`),
  })

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Describe the campaign</h1>
            <div className="page-sub">
              Write the brief the way you would explain it to a colleague. Anything you leave out
              will be asked for, not assumed.
            </div>
          </div>
        </div>

        {parse.isError ? <ErrorCallout error={parse.error} /> : null}

        <div className="card">
          <div className="card-body stack">
            <label className="field">
              Campaign brief
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={5}
                placeholder="Launch campaign for … in … targeting … Budget … Starting …"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) {
                    e.preventDefault()
                    parse.mutate(text)
                  }
                }}
              />
            </label>
            <div className="row between">
              <span className="tiny faint">⌘↵ to submit</span>
              <button
                className="primary"
                disabled={!text.trim() || parse.isPending}
                onClick={() => parse.mutate(text)}
              >
                {parse.isPending ? 'Reading the brief…' : 'Interpret the brief'}
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Start from an example</h2>
          </div>
          <div className="card-body grid two">
            {EXAMPLES.map((example) => (
              <button
                key={example.title}
                className="card"
                style={{ textAlign: 'left', padding: 12, cursor: 'pointer', whiteSpace: 'normal' }}
                onClick={() => setText(example.text)}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{example.title}</div>
                <div className="tiny muted">{example.text}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Recent plans</h2>
          </div>
          {plans.isLoading ? (
            <div className="card-body">
              <Skeleton rows={3} />
            </div>
          ) : plans.isError ? (
            <div className="card-body">
              <ErrorCallout error={plans.error} onRetry={() => void plans.refetch()} />
            </div>
          ) : !plans.data?.length ? (
            <EmptyState title="No plans yet">
              Interpret a brief above to produce your first ranked plan.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Status</th>
                    <th className="num">Versions</th>
                    <th className="num">Assets</th>
                    <th className="num">Cost</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {plans.data.map((plan) => {
                    const latest = plan.versions[plan.versions.length - 1]
                    return (
                      <tr key={plan.plan_id}>
                        <td style={{ fontWeight: 550 }}>{plan.name}</td>
                        <td>
                          <span
                            className={`chip ${plan.status === 'approved' || plan.status === 'handed_off'
                                ? 'ok'
                                : plan.status === 'submitted'
                                  ? 'warn'
                                  : 'neutral'
                              }`}
                          >
                            {plan.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="num">{plan.versions.length}</td>
                        <td className="num">{latest?.asset_count ?? '—'}</td>
                        <td className="num">{moneyShort(latest?.total_cost_minor)}</td>
                        <td className="small muted nowrap">{shortDate(latest?.created_at)}</td>
                        <td>
                          <button
                            className="small"
                            onClick={() => navigate(`/plans/${plan.plan_id}`)}
                          >
                            Open
                          </button>{' '}
                          <button
                            className="small"
                            onClick={() => navigate(`/recommendations/${plan.brief_id}`)}
                            title="Run a new recommendation for this plan's brief, then save it as a new version"
                          >
                            New recommendation
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Callout tone="info" title="How a recommendation is produced">
          Eligibility filters remove assets that cannot be booked at all. Each survivor is enriched
          inside a 500 m buffer, scored deterministically at a pinned scoring version, then a
          budget-constrained optimizer assembles the bundle. Every number you see carries its
          source, freshness and confidence, and anything without data is shown as unavailable
          rather than as zero.
        </Callout>
      </div>
    </main>
  )
}
